import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { listPayments, getRevenueSummary } from '../services/adminApi';
import { formatCurrency, formatDate } from '../utils/formatters';
import StatusChip from '../components/common/StatusChip';

const REVENUE_CARDS = [
  { key: 'total_revenue', label: 'Total Revenue', icon: AttachMoneyIcon, color: '#16a34a' },
  { key: 'intern_payouts', label: 'Intern Payouts', icon: AccountBalanceIcon, color: '#2563eb' },
  { key: 'company_revenue', label: 'Company Revenue', icon: TrendingUpIcon, color: '#7c3aed' },
  { key: 'referral_bonuses', label: 'Referral Bonuses', icon: CardGiftcardIcon, color: '#d97706' },
];

export default function PaymentsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([listPayments(), getRevenueSummary()])
      .then(([paymentsData, summaryData]) => {
        // Always coerce to array — API can return {items:[]}, {payments:[]},
        // a bare array, or a non-iterable object. .map on non-array = crash.
        const candidates = [
          paymentsData?.payments,
          paymentsData?.items,
          paymentsData,
        ];
        const arr = candidates.find(Array.isArray) || [];
        setPayments(arr);
        setSummary(summaryData || {});
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    {
      field: 'payment_id',
      headerName: 'Payment ID',
      width: 150,
      valueGetter: (params) => params.row.payment_id || params.row.id || '-',
    },
    {
      field: 'assessment_id',
      headerName: 'Assessment',
      width: 150,
      valueGetter: (params) => params.row.assessment_id || '-',
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 130,
      valueGetter: (params) => params.row.type || params.row.payment_type || '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => {
        const statusColors = {
          succeeded: '#16a34a',
          pending: '#ed6c02',
          failed: '#dc2626',
          refunded: '#6b7280',
        };
        const color = statusColors[params.value] || '#6b7280';
        return (
          <Box
            component="span"
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.8rem',
              fontWeight: 600,
              backgroundColor: `${color}18`,
              color: color,
              border: `1px solid ${color}40`,
            }}
          >
            {params.value?.charAt(0).toUpperCase() + params.value?.slice(1) || '-'}
          </Box>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 170,
      valueFormatter: (params) => formatDate(params.value),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Payments
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {REVENUE_CARDS.map((card) => {
          const Icon = card.icon;
          const value = summary[card.key] ?? 0;

          return (
            <Grid item xs={12} sm={6} md={6} lg={3} key={card.key}>
              <Paper sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: 0 }}>
                    {card.label}
                  </Typography>
                  <Icon sx={{ color: card.color, fontSize: 28, flexShrink: 0 }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: card.color,
                    fontSize: 'clamp(1.05rem, 4vw, 2.125rem)',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    
                  }}
                  title={formatCurrency(value)}
                >
                  {formatCurrency(value)}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {isMobile ? (
        <Stack spacing={1.5}>
          {payments.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No payments yet</Typography>
            </Paper>
          )}
          {payments.map((p) => (
            <Paper key={p.id || p.payment_id || Math.random()} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {formatCurrency(p.amount || 0)}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                  {p.status || '—'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }} noWrap>
                {(p.payment_id || p.id || '').slice(0, 14)}…
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {p.type || p.payment_type || '—'} · {formatDate(p.created_at)}
              </Typography>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Box sx={{ height: 500, backgroundColor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={payments}
            columns={columns}
            getRowId={(row) => row.id || row.payment_id || Math.random()}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            disableRowSelectionOnClick
          />
        </Box>
      )}
    </Box>
  );
}
